import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import lodash from 'lodash';
import moment from 'moment';
import { Typography, CircularProgress, Paper } from '@material-ui/core';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import PinDropIcon from '@mui/icons-material/PinDrop';
import HistoryIcon from '@mui/icons-material/History';
import {
  returnLoadingText,
  geocodeAddress,
  mapOptions,
  checkIfGooglePlacesSessionValid,
  getTailoredRegionByTimezone,
  getGoogleLoader,
} from 'src/utils/functions';
import { AddressObj, EstimatorRegions } from 'src/interfaces/idashboard';
import { Button, Grid, Modal, TextField } from '@mui/material';
import {
  BACKGROUND_COLOR,
  COLOR_ACCENT_1,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
} from 'src/utils/const';
import { User } from 'src/interfaces/iuser';

import CloseIcon from '@material-ui/icons/Close';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import { SearchAddressDetailsType } from '../../../propTypes';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/keys';

const RIPPLE_SIZE = 100;
const RIPPLE_COLOR = COLOR_ACCENT_1;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    options: {
      backgroundColor: '#fff',
      border: 'none',
      '&:hover': {
        fontColor: '#abcdef',
        textColor: '#abcdef',
        backgroundColor: '#fff',
        border: 'none',
      },
    },
    autoCompleteInput: {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          border: 'none',
        },
      },
    },
    paperModal: {
      backgroundColor: BACKGROUND_COLOR,
      width: 600,
      padding: theme.spacing(2),
      animation: 'fade-scale-in 0.3s ease forwards',
      //tablet, mobile
      [theme.breakpoints.down(900)]: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        boxSizing: 'border-box',
      },
    },
    iconButton: {
      [theme.breakpoints.down(900)]: {
        position: 'relative',
        zIndex: 100,
        background: '#fff !important',
        borderRadius: '50%',
        height: 70,
        width: 70,
        boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.55)',
        padding: 10,
        color: '#000',
        transform: 'scale(0.7) translateY(-20px)',
      },
    },
    customLocationMapContainer: {
      height: 400,
      [theme.breakpoints.down(900)]: {
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
      },
    },
    button: {
      background: COLOR_PRIMARY_DARK,
      borderRadius: 5,
      color: 'white',
      padding: '5px 30px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
      fontSize: 16,
    },
    selectLoactionButton: {
      [theme.breakpoints.down(900)]: {
        position: 'absolute',
        bottom: 25,
        width: '80%',
        left: '10%',
        right: '10%',
        borderRadius: 100,
        boxSizing: 'border-box',
      },
    },
    ripple: {
      position: 'absolute',
      top: 0,
      left: 0,
      marginTop: -RIPPLE_SIZE / 2,
      marginLeft: -RIPPLE_SIZE / 2,
      width: RIPPLE_SIZE,
      height: RIPPLE_SIZE,
      animation: `$rippleAnimation 1000ms ${theme.transitions.easing.easeOut}`,
      animationIterationCount: 'infinite',
      opacity: 0,
      borderRadius: '50%',
      boxShadow: `0 0 6px 3px ${RIPPLE_COLOR}`,
    },
    '@keyframes rippleAnimation': {
      '0%': {
        transform: 'scale(0.1, 0.1)',
        opacity: 0,
      },
      '1%': {
        opacity: 1,
      },
      '50% ': {
        opacity: 1,
      },
      '100%': {
        transform: 'scale(1.2, 1.2)',
        opacity: 0,
      },
    },
  })
);

const SearchAutoComplete = ({
  user,
  matches,
  dummyMarket,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  setAddressDetails,
}: {
  user: User;
  matches: boolean;
  dummyMarket: any;
  googleAutocompleteSessionToken: any;
  getGoogleAutocompleteSessionToken: any;
  clearGoogleAutocompleteSessionToken: any;
  googleAutocompleteSessionTokenTimeStamp: any;
  setAddressDetails: React.Dispatch<React.SetStateAction<SearchAddressDetailsType | null>>;
}) => {
  const classes = useStyles();

  const sampleEstimatorMarket = dummyMarket?.address;
  const sampleEstimatorCenterCoord = dummyMarket?.coordinates;

  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<AddressObj[]>([] as AddressObj[]);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(true);

  const [centerCoord, setCenterCoord] = useState<any>(dummyMarket?.coordinates);
  const [address, setAddress] = useState<string>('');
  const [prevCenterCoord, setPrevCenterCoord] = useState<any>(dummyMarket?.coordinates);

  const [locationAllowed, setLocationAllowed] = useState<boolean>(false);
  const [customCenterLat, setCustomCenterLat] = useState(39.210878338011376); // default to US approx center
  const [customCenterLng, setCustomCenterLng] = useState(-101.51684523289367); // default to US approx center
  const [searchBarState, setSearchBarState] = useState<string>('');
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [geocodingLoading, setGeocodingLoading] = useState<boolean>(false);

  const [circleCopy, setCircleCopy] = useState<any>(null);
  const [radiusInMeter, setRadiusInMeter] = useState<number>(5000);

  const [customLocationModalOpen, setCustomLocationModalOpen] = useState<boolean>(false);

  const [searchBarValue, setSearchBarValue] = useState<AddressObj>({
    address: '',
  } as AddressObj);

  const loading: boolean = open && loadingState;
  const emptySearchBar: boolean = open && searchBarState.length === 0;

  const handleInputChange = useCallback(
    async (event, value: string) => {
      setSearchBarState(value);
      setLoadingState(true);
      let addressObjs: AddressObj[] = [];

      let sessionToken = googleAutocompleteSessionToken;

      const isGooglePlacesNeeded: boolean = !checkIfGooglePlacesSessionValid(
        googleAutocompleteSessionTokenTimeStamp
      );

      const google = (window as any).google;
      if (!google || !google.maps || isGooglePlacesNeeded) {
        const loader = await getGoogleLoader();
        sessionToken = await getGoogleAutocompleteSessionToken(loader);
      }

      if (!sessionToken) return;

      const service = new google.maps.places.AutocompleteService();

      try {
        if (value.length > 0) {
          await service.getPlacePredictions(
            { input: `${value}`, sessionToken: sessionToken },
            (results, status) => {
              if (status == google.maps.places.PlacesServiceStatus.OK && results) {
                results.forEach(result => {
                  addressObjs.push({
                    address: result.description,
                    shortAddress: result.structured_formatting?.main_text,
                  });
                });
              }
            }
          );
          logAmplitudeEvent('report_creation_attempted', { search_term: value });
        }
      } catch (error) {
        console.log('error in estimator autocomplete ', error);
      }

      setOptions(e => {
        let tempAddressObjs: AddressObj[] = [...addressObjs];
        tempAddressObjs.push({ address: 'Set location on map', isDefaultOption: true });

        return tempAddressObjs;
      });

      setLoadingState(false);
    },
    [googleAutocompleteSessionToken]
  );

  const handleSearchChange = useCallback(
    async (event: any, value: AddressObj) => {
      setSearchBarValue({
        address: value?.address as string,
      });

      if (!value) {
        return;
      }

      if (value.isDefaultOption) {
        setCustomLocationModalOpen(true);
        return;
      }

      if (value.isCustom) {
        const latString = value.address.split(',')[0].split('(')[1];
        const lngString = value.address.split(',')[1].split(')')[0];
        setCenterCoord({ lat: parseFloat(latString), lng: parseFloat(lngString) });
        setAddress('');
        return;
      }

      setGeocodingLoading(true);
      const [lat, lng] = (await geocodeAddress(value.address)) as [number, number];
      if (!isNaN(lat) && !isNaN(lng)) {
        setCenterCoord({ lat, lng });
        setAddress(value.address);

        setAddressDetails({
          address_full: value.address,
          address_short: value?.shortAddress || '',
          latitude: lat,
          longitude: lng,
        });
      }
      setGeocodingLoading(false);
      clearGoogleAutocompleteSessionToken();
    },
    [user.estimatorRegions]
  );

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      position => {
        setCustomCenterLat(position.coords.latitude);
        setCustomCenterLng(position.coords.longitude);
        setLocationAllowed(true);
      },
      error => {
        console.error('Error Code = ' + error.code + ' - ' + error.message);
      }
    );
  }, []);

  const defaultCoord = getTailoredRegionByTimezone();

  const getDefaultCenterLocation = () => {
    return defaultCoord ? defaultCoord : { lat: customCenterLat, lng: customCenterLng };
  };

  const setLocation = () => {
    setCustomLocationModalOpen(false);
    setCenterCoord({ lat: customCenterLat, lng: customCenterLng });
    setSearchBarValue({ address: `(${customCenterLat}, ${customCenterLng})` });
    setAddressDetails({
      address_full: 'Custom Address',
      address_short: 'Custom Address',
      latitude: customCenterLat,
      longitude: customCenterLng,
    });
  };

  const locationPin = useMemo(() => {
    return (
      <div>
        <div className={classes.ripple}></div>
        <PinDropIcon
          style={{
            fontSize: 40,
            position: 'absolute',
            top: -20,
            left: -20,
          }}
        />
      </div>
    );
  }, [customCenterLat, customCenterLng]);

  // set custom location
  const setCustomLocationApiIsLoaded = (map, maps) => {
    const drawingManager = new maps.drawing.DrawingManager({
      drawingControl: false,
      circleOptions: {
        editable: true,
        draggable: false,
        clickble: false,
      },
    });

    drawingManager.setMap(map);

    const circle: any = new google.maps.Circle({
      strokeColor: '#000022',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#000022',
      fillOpacity: 0.35,
      map: map,
      radius: 5000,
      center: map.getCenter(),
      editable: true,
    });

    setCircleCopy(circle);

    map.addListener('dragend', () => {
      circle.setCenter(map.getCenter());
    });

    circle.addListener('radius_changed', () => {
      setRadiusInMeter(circle.radius);
    });

    circle.addListener('center_changed', () => {
      const centerLat = circle.center.lat();
      const centerLng = circle.center.lng();
      setRadiusInMeter(circle.radius);
      setCustomCenterLat(centerLat);
      setCustomCenterLng(centerLng);
    });
  };

  const handleCustomLocationMapChange = useCallback(({ center }) => {
    const centerLat: number = center.lat();
    const centerLng: number = center.lng();
    setCustomCenterLat(centerLat);
    setCustomCenterLng(centerLng);
  }, []);

  return (
    <>
      <div style={{ width: '100%', paddingTop: '15px' }}>
        <Autocomplete
          id="estimator-search"
          open={open}
          classes={{
            option: classes.options,
          }}
          onOpen={() => {
            setOpen(true);
            setOptions(e => {
              let tempAddressObjs: AddressObj[] = [
                { address: 'Set location on map', isDefaultOption: true },
              ];

              return tempAddressObjs;
            });
          }}
          onClose={() => {
            setOpen(false);
          }}
          filterOptions={(options, params) => {
            return options;
          }}
          getOptionLabel={option => {
            if (option.isDefaultOption) return `(${centerCoord.lat}, ${centerCoord.lng})`;
            return option.address;
          }}
          renderOption={(props, option) => {
            if (props.isDefaultOption) {
              return (
                <Typography>
                  <IconButton
                    style={{
                      paddingTop: 3,
                      paddingBottom: 3,
                      paddingLeft: 0,
                    }}
                  >
                    <PinDropIcon
                      style={{
                        fontSize: 40,
                        display: 'inline-block',
                        marginBottom: '3px',
                      }}
                    />
                  </IconButton>
                  Set location on map
                </Typography>
              );
            }
            if (props.isSearchedOption) {
              return (
                <Typography>
                  <IconButton
                    style={{
                      paddingTop: 3,
                      paddingBottom: 3,
                      paddingLeft: 0,
                    }}
                  >
                    <HistoryIcon
                      style={{
                        fontSize: 40,
                        display: 'inline-block',
                        marginBottom: '3px',
                      }}
                    />
                  </IconButton>
                  {props.address}
                  <span style={{ marginLeft: '15px', color: '#ccc', fontSize: '15px' }}>
                    {props?.daysLeft}
                  </span>
                </Typography>
              );
            } else {
              return props.address;
            }
          }}
          options={options}
          loading={loading}
          onChange={(event, value) => handleSearchChange(event, value as AddressObj)} // this one being called after user click the value in the dropdown
          onInputChange={lodash.debounce((value, event) => handleInputChange(value, event), 600)} // this one being called whenever user inputs a letter
          noOptionsText={'No search result'}
          loadingText={returnLoadingText(emptySearchBar)}
          defaultValue={{ address: sampleEstimatorMarket }}
          value={searchBarValue}
          renderInput={params => (
            <TextField
              {...params}
              //   label="Search for an address"
              classes={{ root: classes.autoCompleteInput }}
              placeholder="Search for an address"
              InputLabelProps={{
                shrink: true,
                color: 'primary',
              }}
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                sx: {
                  '&': {
                    color: '#000',
                    fontWeight: '500',
                    opacity: 1,
                  },
                },
                endAdornment: (
                  <>
                    {!emptySearchBar && loading ? (
                      <CircularProgress color="primary" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </div>

      <Modal open={customLocationModalOpen} onClose={() => setCustomLocationModalOpen(false)}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: '100vh' }}
        >
          <div className={`${classes.paperModal} modal-custom`}>
            <Grid
              container
              direction={matches ? 'column' : 'row'}
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <IconButton
                className={classes.iconButton}
                onClick={() => setCustomLocationModalOpen(false)}
              >
                <CloseIcon style={{ color: '#f00', fontSize: 40 }} />
              </IconButton>
              <IconButton className={classes.iconButton} onClick={() => setIsInfoModalOpen(true)}>
                <NotListedLocationIcon style={{ color: '#000', fontSize: 40 }} />
              </IconButton>
            </Grid>
            <Paper className={classes.customLocationMapContainer}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: GOOGLE_MAPS_API_KEY,
                  libraries: ['drawing', 'places', 'visualization'],
                  id: 'calculator-search-page',
                  v: '3.64',
                } as any}
                center={getDefaultCenterLocation()}
                options={{
                  ...mapOptions,
                  fullscreenControl: matches ? false : true,
                  zoomControlOptions: { position: matches ? 4 : 9 },
                }}
                zoom={8}
                resetBoundsOnResize
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => setCustomLocationApiIsLoaded(map, maps)}
                onDragEnd={map => handleCustomLocationMapChange(map)}
              >
                {!circleCopy && locationPin}
              </GoogleMapReact>
            </Paper>

            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginTop: 10 }}
            >
              <Typography>Move the map</Typography>

              {matches && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '100px',
                    background: '#fff',
                    borderRadius: '50px 50px 0 0',
                    boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.25)',
                  }}
                ></div>
              )}

              <Button
                size="small"
                variant="outlined"
                className={`${classes.button} ${classes.selectLoactionButton}`}
                onClick={() => {
                  setLocation();
                }}
              >
                Set Location
              </Button>
            </Grid>
          </div>
        </Grid>
      </Modal>
    </>
  );
};

export default SearchAutoComplete;
